import {
  buildTrapetsScreeningInput,
  type ScreeningSubject,
  type TrapetsLookupInput,
  type TrapetsRepresentativeInput,
  type TrapetsRepresentativeResult,
  type TrapetsSearchResult,
  type TrapetsStatus,
} from "@repo/shared/types";
import { useEffect, useState, useTransition } from "react";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import type { ComponentStyleType } from "@/lib/types/flow";
import { TrapetsLookupForm } from "./trapets-lookup-form";
import { TrapetsLookupResult } from "./trapets-lookup-result";
import { T } from "./trapets-lookup-widget.i18n";

// Snapshot structure - supports both Person and Company
export type TrapetsLookupSnapshot = {
  clientType: "Person" | "Company" | "Group";
  input: {
    name: string;
    country: string;
    // Person fields
    ssn?: string;
    ssnWithPrefix?: string;
    birthDate?: string;
    // Company fields
    orgId?: string;
    // QL-654 — every representative that was screened
    representatives?: TrapetsRepresentativeInput[];
  };
  // `false` after a test→production switch invalidated the marker while keeping the result.
  hasBeenChecked: boolean;
  searchedAt: string;
  rawResults: TrapetsSearchResult[];
  status: TrapetsStatus;
  isTestMode?: boolean;
  /** QL-21 follow-up — the representative's person search for a Company/Group,
   *  which is where the PEP answer comes from. */
  representativeStatus?: TrapetsStatus | null;
  representativeRawResults?: TrapetsSearchResult[];
  subject?: ScreeningSubject;
  /** QL-654 — every person screened for PEP, in order. */
  subjects?: ScreeningSubject[];
  /** QL-654 — each representative's own search result. */
  representatives?: TrapetsRepresentativeResult[];
};

/** QL-654 — one representative's section of the form. */
export type RepresentativeFormState = {
  /** Stable React key. */
  key: string;
  /** The `client_party.id` / member id they are registered under, when they are. */
  partyId: string | null;
  name: string;
  ssn: string;
  birthDate: string;
  idMode: IdMode;
  country: string;
};

/** QL-654 — a registered representative, to prefill a section from. */
export type RepresentativePrefill = {
  id: string;
  name: string;
  ssn: string | null;
  dateOfBirth: string | null;
  country: string | null;
};

let repKeyCounter = 0;
const nextRepKey = () => `rep-${++repKeyCounter}`;

const emptyRep = (country: string): RepresentativeFormState => ({
  key: nextRepKey(),
  partyId: null,
  name: "",
  ssn: "",
  birthDate: "",
  idMode: "ssn",
  country,
});

/** The form sections for the registered representatives (at least one, possibly empty). */
function repsFromPrefill(
  prefill: readonly RepresentativePrefill[],
  clientCountry: string,
): RepresentativeFormState[] {
  if (prefill.length === 0) return [emptyRep(clientCountry)];
  return prefill.map((r) => ({
    key: nextRepKey(),
    partyId: r.id,
    name: r.name,
    ssn: r.ssn || "",
    birthDate: r.dateOfBirth || "",
    idMode: preferredIdMode(r.ssn, r.dateOfBirth),
    country: r.country || clientCountry,
  }));
}

/** Representatives of a saved `inputData`: the list when present, else the legacy single fields. */
function repsFromSavedInput(
  rawInput: string | null | undefined,
  clientCountry: string,
): RepresentativeFormState[] {
  if (!rawInput) return [];
  try {
    const parsed = JSON.parse(rawInput) as TrapetsLookupInput;
    const list: TrapetsRepresentativeInput[] = parsed.representatives?.length
      ? parsed.representatives
      : parsed.representativeName
        ? [
            {
              name: parsed.representativeName,
              ssn: parsed.representativeSsn,
              birthDate: parsed.representativeBirthDate,
              country: parsed.representativeCountry,
            },
          ]
        : [];
    return list.map((r) => ({
      key: nextRepKey(),
      partyId: r.partyId ?? null,
      name: r.name,
      ssn: r.ssn || "",
      birthDate: r.birthDate || "",
      idMode: preferredIdMode(r.ssn, r.birthDate),
      country: r.country || clientCountry,
    }));
  } catch {
    return [];
  }
}

/** QL-c — which identifier a person is screened by. */
export type IdMode = "ssn" | "dob";

/**
 * Which identifier to offer first.
 *
 * A national id wins when we hold one: it resolves to an authoritative name and
 * birth date, where a typed name+DOB is only as good as what was entered. With
 * no id but a date of birth on file, that is what we can actually screen by, so
 * preselect it rather than opening on an empty field the advisor has to
 * abandon. With neither, fall back to the national id — the commoner case, and
 * the one whose field the advisor is most likely to be able to fill.
 */
export function preferredIdMode(
  ssn: string | null | undefined,
  dateOfBirth: string | null | undefined,
): IdMode {
  if (ssn?.trim()) return "ssn";
  if (dateOfBirth?.trim()) return "dob";
  return "ssn";
}

type TrapetsLookupWidgetProps = {
  clientType: "Person" | "Company" | "Group";
  clientName: string | null;
  clientCountry: string | null;
  clientSSN: string | null;
  /** QL-c — the alternative identifier, for clients with no national id. */
  clientDateOfBirth: string | null;
  clientOrgId: string | null;
  inputData?: string | null;
  calculatedData?: string | null;
  onSearch: (input: TrapetsLookupInput) => Promise<TrapetsLookupSnapshot>;
  disabled?: boolean;
  style?: ComponentStyleType;
  title?: string;
  description?: string;
  language?: string;
  hasValidationError?: boolean;
  /** QL-21 follow-up / QL-654 — the client's registered representatives, used to
   *  prefill the representative half of a Company/Group screening (one section
   *  each). The advisor can change them; searching writes them back to Related
   *  parties. */
  representatives?: readonly RepresentativePrefill[];
};

export function TrapetsLookupWidget({
  clientType,
  clientName,
  clientCountry,
  clientSSN,
  clientDateOfBirth,
  clientOrgId,
  inputData,
  calculatedData,
  onSearch,
  disabled = false,
  style = "none",
  title,
  description,
  language,
  hasValidationError = false,
  representatives,
}: TrapetsLookupWidgetProps) {
  const { userLanguage } = useUserLanguage();
  const lang = language || userLanguage;
  const t = useT(T);
  const [isPending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);

  const isCompany = clientType === "Company";
  // A Group is company-like for the form: it has no SSN of its own, and its
  // screening runs against the representative the server resolves from
  // client_party. The previous `isCompany`-only check sent a Group down the
  // Person branch, which made the advisor type a natural person's national id
  // into the group's own SSN field.
  const isGroup = clientType === "Group";
  const isCompanyLike = isCompany || isGroup;

  // Form state
  const [name, setName] = useState(clientName || "");
  const [country, setCountry] = useState(clientCountry || "NO");
  // Person-specific. QL-c — a person is screened by national id OR by date of
  // birth; `idMode` says which, and only the selected one is sent.
  const [ssn, setSSN] = useState(clientSSN || "");
  const [birthDate, setBirthDate] = useState(clientDateOfBirth || "");
  const [idMode, setIdMode] = useState<IdMode>(() => preferredIdMode(clientSSN, clientDateOfBirth));
  // Company-specific
  const [orgId, setOrgId] = useState(clientOrgId || "");
  // QL-21 follow-up / QL-654 — the representatives screened on a Company/Group's
  // behalf. Prefilled from Related parties; registered people are shown for screening (screening never edits them), and typed people are registered only when the client has none.
  const initialReps = () => {
    const registered = representatives ?? [];
    if (registered.length > 0) return repsFromPrefill(registered, clientCountry || "NO");
    const saved = repsFromSavedInput(inputData, clientCountry || "NO");
    return saved.length > 0 ? saved : [emptyRep(clientCountry || "NO")];
  };
  const [reps, setReps] = useState<RepresentativeFormState[]>(initialReps);
  // Keyed by the form row's own key (not its index), so removing a section
  // cannot move an error onto another representative.
  const [repErrors, setRepErrors] = useState<Record<string, boolean>>({});
  const updateRep = (index: number, patch: Partial<RepresentativeFormState>) =>
    setReps((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  const addRep = () => setReps((prev) => [...prev, emptyRep(clientCountry || "NO")]);
  const removeRep = (index: number) => {
    const removed = reps[index];
    setReps((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
    if (removed) {
      setRepErrors((prev) => {
        const { [removed.key]: _gone, ...rest } = prev;
        return rest;
      });
    }
  };

  const [showForm, setShowForm] = useState(false);

  // Local snapshot state — merge inputData + calculatedData into the display shape
  const buildSnapshot = (rawInput: string, rawCalculated: string): TrapetsLookupSnapshot | null => {
    try {
      const parsed = JSON.parse(rawInput);
      const calculated = JSON.parse(rawCalculated);
      return {
        clientType: parsed.clientType,
        input: {
          name: parsed.name,
          country: parsed.country,
          ssn: parsed.ssn,
          ssnWithPrefix: parsed.ssnWithPrefix,
          birthDate: parsed.birthDate,
          orgId: parsed.orgId,
          representatives: parsed.representatives,
        },
        ...calculated,
      };
    } catch {
      return null;
    }
  };

  const [localSnapshot, setLocalSnapshot] = useState<TrapetsLookupSnapshot | null>(() => {
    if (inputData && calculatedData) {
      return buildSnapshot(inputData, calculatedData);
    }
    return null;
  });

  useEffect(() => {
    if (inputData && calculatedData) {
      const snapshot = buildSnapshot(inputData, calculatedData);
      if (snapshot) setLocalSnapshot(snapshot);
    }
  }, [inputData, calculatedData]);

  const snapshot = localSnapshot;

  // The registered representatives as a STABLE key: the form is re-seeded only
  // when who they are changes, never on a re-render, so what the advisor has
  // typed is not wiped.
  const representativesKey = JSON.stringify(
    (representatives ?? []).map((r) => [r.id, r.name, r.ssn, r.dateOfBirth, r.country]),
  );

  // Initialize form from client data
  useEffect(() => {
    // Also when the form is reopened over an existing result ("Re-run"), so it
    // shows who is registered NOW rather than what was typed last time.
    if (!snapshot || showForm) {
      setName(clientName || "");
      setCountry(clientCountry || "NO");
      if (isCompanyLike) {
        setOrgId(clientOrgId || "");
        setReps(repsFromPrefill(representatives ?? [], clientCountry || "NO"));
      } else {
        setSSN(clientSSN || "");
        setBirthDate(clientDateOfBirth || "");
        setIdMode(preferredIdMode(clientSSN, clientDateOfBirth));
      }
    }
  }, [
    clientName,
    clientCountry,
    clientSSN,
    clientOrgId,
    snapshot,
    showForm,
    isCompanyLike,
    representativesKey,
  ]);

  const handleSearch = async () => {
    setError(null);

    // QL-277 — the same builder chat uses, so the form and the AI search the
    // same thing. Only the SELECTED identifier is passed: the builder prefers a
    // national id, so a deliberate switch to date of birth would otherwise be
    // silently ignored.
    const built = buildTrapetsScreeningInput(
      {
        name,
        type: clientType,
        ssn: idMode === "ssn" ? ssn : null,
        dateOfBirth: idMode === "dob" ? birthDate : null,
        orgId,
        country,
      },
      isCompanyLike
        ? reps.map((r) => ({
            id: r.partyId ?? undefined,
            name: r.name,
            ssn: r.idMode === "ssn" ? r.ssn : null,
            dateOfBirth: r.idMode === "dob" ? r.birthDate : null,
            country: r.country,
          }))
        : [],
    );
    if (!built.ok) {
      // Mark the sections that cannot be screened (no name, or no identifier).
      setRepErrors(
        isCompanyLike
          ? Object.fromEntries(
              reps.map((r) => [
                r.key,
                !r.name.trim() || !(r.idMode === "ssn" ? r.ssn : r.birthDate).trim(),
              ]),
            )
          : {},
      );
      setError(
        {
          missing_name: t.nameRequired,
          missing_country: t.countryRequired,
          missing_person_id: idMode === "ssn" ? t.ssnRequired : t.birthDateRequired,
          missing_representative: t.representativeRequired,
          // Unreachable here: the form passes no overrides.
          incomplete_representative_override: t.representativeRequired,
        }[built.reason],
      );
      return;
    }
    setRepErrors({});
    const { input } = built;

    startTransition(async () => {
      try {
        const newSnapshot = await onSearch(input);
        setLocalSnapshot(newSnapshot);
        setShowForm(false);
      } catch (err: any) {
        setError(err.message || t.unexpectedError);
      }
    });
  };

  // Render results view
  if (snapshot && !showForm) {
    return (
      <TrapetsLookupResult
        snapshot={snapshot}
        onRerunSearch={() => setShowForm(true)}
        isPending={isPending}
        disabled={disabled}
        style={style}
        title={title}
        description={description}
        lang={lang}
      />
    );
  }

  // Render search form
  return (
    <TrapetsLookupForm
      isCompany={isCompanyLike}
      isGroup={isGroup}
      reps={reps}
      updateRep={updateRep}
      addRep={addRep}
      removeRep={removeRep}
      repErrors={repErrors}
      name={name}
      setName={setName}
      country={country}
      setCountry={setCountry}
      ssn={ssn}
      setSSN={setSSN}
      idMode={idMode}
      setIdMode={setIdMode}
      birthDate={birthDate}
      setBirthDate={setBirthDate}
      orgId={orgId}
      setOrgId={setOrgId}
      onSearch={handleSearch}
      isPending={isPending}
      disabled={disabled}
      error={error}
      style={style}
      title={title}
      description={description}
      hasValidationError={hasValidationError}
    />
  );
}
